import { AlertTriangle, ChevronDown, X } from 'lucide-react';
import { useMemo, useState } from 'react';

import { validatePack, type PackIssue } from '@/lib/pack-validate';
import { cn } from '@/lib/utils';
import { useEditorActions, useEditorStore } from '@/store/editor.store';

/**
 * Non-blocking, pack-mode-only banner under the TopBar. Surfaces structural
 * issues from `validatePack()` — orphan slides, dangling pointers, ghost /
 * half-ghost quizzes, quiz consistency. Click-through opens a drawer
 * listing each issue with a "go to slide" jump.
 *
 * Saves are never blocked — teachers can keep editing and resolve as they go.
 */
export function ValidationBanner() {
  const mode = useEditorStore((s) => s.mode);
  const slides = useEditorStore((s) => s.script?.slides ?? null);
  const entryBeatId = useEditorStore((s) => s.packMeta.entryBeatId ?? null);
  const actions = useEditorActions();
  const [open, setOpen] = useState(false);

  const issues = useMemo(
    () => (slides ? validatePack(slides, { entryBeatId }) : []),
    [slides, entryBeatId],
  );

  if (mode !== 'pack' || !slides || issues.length === 0) return null;

  const errors = issues.filter((i) => i.severity === 'error').length;
  const warnings = issues.length - errors;

  return (
    <div className="border-b border-amber-200 bg-amber-50/70">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 px-5 py-2 text-[12.5px] text-amber-900 transition hover:bg-amber-100/60"
      >
        <AlertTriangle size={14} className="shrink-0 text-amber-700" />
        <span className="font-semibold">
          This pack has {issues.length} issue{issues.length === 1 ? '' : 's'}
        </span>
        <span className="text-[11.5px] text-amber-800/80">
          {errors > 0 && (
            <>
              {errors} error{errors === 1 ? '' : 's'}
              {warnings > 0 && ' · '}
            </>
          )}
          {warnings > 0 && (
            <>
              {warnings} warning{warnings === 1 ? '' : 's'}
            </>
          )}
          {' — '}
          fix before publishing.
        </span>
        <ChevronDown
          size={13}
          className={cn(
            'ml-auto shrink-0 text-amber-700 transition-transform',
            open && 'rotate-180',
          )}
        />
      </button>

      {open && (
        <div className="max-h-[280px] overflow-y-auto border-t border-amber-200 bg-white scroll-thin">
          <ul className="divide-y divide-border-subtle">
            {issues.map((issue, i) => (
              <IssueRow
                key={`${issue.kind}-${issue.slideId}-${i}`}
                issue={issue}
                onJump={() => {
                  actions.setSelection({ kind: 'slide', slideId: issue.slideId });
                  setOpen(false);
                }}
              />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function IssueRow({ issue, onJump }: { issue: PackIssue; onJump: () => void }) {
  return (
    <li className="flex items-start gap-2.5 px-5 py-2.5">
      <span
        className={cn(
          'mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded',
          issue.severity === 'error'
            ? 'bg-red-100 text-red-700'
            : 'bg-amber-100 text-amber-700',
        )}
      >
        {issue.severity === 'error' ? <X size={11} /> : <AlertTriangle size={11} />}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[12.5px] font-semibold text-fg">{issue.title}</div>
        <div className="mt-0.5 text-[11.5px] leading-snug text-fg-muted">
          {issue.detail}
        </div>
      </div>
      <button
        type="button"
        onClick={onJump}
        className="shrink-0 rounded-md border border-border bg-white px-2 py-1 text-[11px] font-medium text-fg-muted transition hover:border-accent hover:text-accent"
      >
        Go to slide
      </button>
    </li>
  );
}
